/**
 * src/components/pipeline/quote/QuoteReviewToaster.jsx — TICKET-1098.
 *
 * App-open reminder (CEO 2026-10-05: "if I miss the toast, bring me back"): once
 * per browser session, the first time the pending-review list loads non-empty,
 * show one toast per quote (max 3) that opens the quote directly, plus a "+N more"
 * toast that jumps to the Pipeline filter. Renders nothing.
 */
import { useEffect, useRef } from 'react';
import usePendingReviews, { reviewLabel } from '../../../lib/hooks/usePendingReviews';
import { requestQuoteOpen } from '../../../lib/nav/quoteNav';
import { useToast } from '../../shared/ToastProvider.jsx';
import { useT } from '../../../lib/i18n/I18nContext';

export const SESSION_KEY = 'acn_quote_review_toast_shown';
const MAX_TOASTS = 3;

function alreadyShown() {
  try { return sessionStorage.getItem(SESSION_KEY) === '1'; } catch { return false; }
}
function markShown() {
  try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* private mode — the ref still guards this mount */ }
}

export default function QuoteReviewToaster({ onShowAll }) {
  const { reviews, enabled, setReviewFilter } = usePendingReviews();
  const { showToast } = useToast();
  const { t } = useT();
  const done = useRef(false);

  useEffect(() => {
    if (done.current || !enabled || !reviews.length || alreadyShown()) return;
    done.current = true;
    markShown();
    for (const r of reviews.slice(0, MAX_TOASTS)) {
      showToast({
        severity: 'warning',
        message: t('quoteReview.toast', { label: reviewLabel(r) }),
        durationMs: 15000,
        action: { label: t('quoteReview.open'), onClick: () => requestQuoteOpen(r.id) },
      });
    }
    const rest = reviews.length - MAX_TOASTS;
    if (rest > 0) {
      showToast({
        severity: 'info',
        message: t('quoteReview.toastMore', { count: rest }),
        durationMs: 15000,
        action: { label: t('quoteReview.showAll'), onClick: () => { setReviewFilter(true); onShowAll?.(); } },
      });
    }
  }, [enabled, reviews, showToast, t, onShowAll, setReviewFilter]);

  return null;
}
